import Http from '../../network/Http'
import {ContentType} from '../../network/Constants'
import {AssocDirector} from './model/AssocDirector'
import {ApiResponse} from '../../network/response/ApiResponse'
import {ResidentUsersSearchConditions} from './model/ResidentUsersSearchConditions'
import {RegisterUsersInfo} from './model/RegisterUsersInfo'
import {formatForDateInputFromDate} from '../../formatters/formatDate'
import {ResidentUsersSearchResult} from './model/ResidentUsersSearchResult'
import {Nullable} from '../../app/TypeHelpers'
import {AcceptedWarning} from '../../__NoAutomatedTests/components/common/admin/NatAcceptedWarning'
import {AssocDirectorPositionInfo} from './model/AssocDirectorPositionInfo'
import {Information} from '../../network/response/Information'
import {RESIDENT_STATUS, RESIDENT_USER_TYPE, ROLE_FOR_ADMINISTRATOR_REAL} from '../../CodeMaster'

export interface RoomToResident {
    ridgeCd: string
    roomNumber: string
    sutekiNetId: string
}

export interface UpdateAssocDirectorInfo {
    availableTermStartDate: Nullable<string>
    availableTermEndDate: Nullable<string>
    assocDirectorVersion: string
    acceptedWarnings: AcceptedWarning[]
}

interface AssocDirectorData extends Omit<AssocDirector, 'availableTermStartDate' | 'availableTermEndDate' | 'assocDirectorPositionInfoList'> {
    availableTermStartDate: string
    availableTermEndDate: string
    assocDirectorPositionInfoList: AssocDirectorPositionInfoData[]
}

interface AssocDirectorPositionInfoData extends Omit<AssocDirectorPositionInfo, 'midtermAppointmentDate' | 'midtermTerminationDate' | 'directorPositionTermStartDate' | 'directorPositionTermEndDate'> {
    midtermAppointmentDate: Nullable<string>
    midtermTerminationDate: Nullable<string>
    directorPositionTermStartDate: string
    directorPositionTermEndDate: string
}

export interface UpdateAssocDirectorPosition {
    midtermAppointmentDate: Nullable<Date>,
    midtermTerminationDate: Nullable<Date>,
    version: number,
}

export const NoAssocTermID = 'NoAssocTermID'

export default class AssocDirectorRepository {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    // 管理組合役員登録
    async registerAssocDirectors(
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        termStartDate: Date,
        termEndDate: Date,
        roomToResidentList: RoomToResident[],
        acceptedWarnings: AcceptedWarning[],
    ): Promise<Information[]> {
        const requestParams: RegisterUsersInfo = {
            roomToResidentList: roomToResidentList,
            availableTermStartDate: formatForDateInputFromDate(termStartDate),
            availableTermEndDate: formatForDateInputFromDate(termEndDate),
            acceptedWarnings: acceptedWarnings,
        }

        const apiResponseJSON: ApiResponse<void> =
            await this.http.post(`/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${assocTermId}/assoc-directors`,
                requestParams,
                ContentType.JSON)

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations
    }

    // 管理組合役員取得
    async getAssocDirectors(
        condominiumCd: string,
        assocId: number,
        assocTermId: number | typeof NoAssocTermID,
    ): Promise<AssocDirector[]> {
        let query: string = ''
        let termId: number
        if (assocTermId === NoAssocTermID) {
            query = '?noAssocTerm=true'
            termId = 0
        } else {
            termId = assocTermId
        }

        const apiResponseJSON: ApiResponse<AssocDirectorData[]> = await this.http.get(`/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${termId}/assoc-directors${query}`)

        const apiResponse: ApiResponse<AssocDirector[]> =
            ApiResponse.fromArray(apiResponseJSON, AssocDirector.from)
        return apiResponse.details
    }

    // 居住者ユーザ検索
    async searchResidentUsers(
        condominiumCd: string,
        assocId: number,
        searchConditions: ResidentUsersSearchConditions,
    ): Promise<ResidentUsersSearchResult> {
        const apiResponseJSON = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/resident-users/search`,
            searchConditions,
            ContentType.JSON,
        )
        const apiResponse: ApiResponse<ResidentUsersSearchResult> =
            ApiResponse.from(
                apiResponseJSON,
                (details) => ({
                    roomResidentInfoList: details.roomResidentInfoList.map((roomResidentInfo: any) => ({
                        ...roomResidentInfo,
                        status: RESIDENT_STATUS.codeToEnumConstants(roomResidentInfo.status),
                        residentUserType: RESIDENT_USER_TYPE.codeToEnumConstants(roomResidentInfo.residentUserType),
                    })),
                    limitOverFlag: details.limitOverFlag,
                }),
            )
        return apiResponse.details
    }

    // 管理組合役員の任期更新
    async updateAssocDirector(
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        assocDirectorId: number,
        updateAssocDirectorInfo: UpdateAssocDirectorInfo,
    ): Promise<Information[]> {
        const apiResponseJSON: ApiResponse<void> = await this.http.put(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${assocTermId}/assoc-directors/${assocDirectorId}`,
            updateAssocDirectorInfo,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations
    }

    // 管理組合役員削除
    async deleteAssocDirector(
        condominiumCd: string,
        assocId: number,
        assocTermId: number | typeof NoAssocTermID,
        assocDirectorId: number,
    ): Promise<Information> {

        const termId = assocTermId === NoAssocTermID ? 0 : assocTermId

        const apiResponseJSON: ApiResponse<void> = await this.http.delete(
            `/api/admin/condominiums/${condominiumCd}/assocs/${assocId}/terms/${termId}/assoc-directors/${assocDirectorId}`,
        )

        const apiResponse: ApiResponse<void> =
            ApiResponse.from(apiResponseJSON)

        return apiResponse.informations[0]
    }

    async registerAssocDirectorPosition(params: {
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        assocDirectorId: number,
        role: ROLE_FOR_ADMINISTRATOR_REAL,
    }): Promise<Information> {
        const apiResponseJSON: ApiResponse<void> = await this.http.post(
            `/api/admin/condominiums/${params.condominiumCd}/assocs/${params.assocId}`
            + `/terms/${params.assocTermId}/assoc-directors/${params.assocDirectorId}/positions`,
            {positionRoleId: params.role.value},
            ContentType.JSON,
        )
        const apiResponse: ApiResponse<void> = ApiResponse.from(apiResponseJSON)

        // TODO 配列の長さチェックを追加するか相談する
        return apiResponse.informations[0]
    }

    formatDateOrNull(date: Nullable<Date>): Nullable<string> {
        return date ? formatForDateInputFromDate(date) : null
    }

    async updateAssocDirectorPosition(params: {
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        assocDirectorId: number,
        assocDirectorPositionId: number,
        updateAssocDirectorPosition: UpdateAssocDirectorPosition,
        acceptedWarnings: AcceptedWarning[],
    }): Promise<Information[]> {
        const {
            midtermAppointmentDate,
            midtermTerminationDate,
            version,
        } = params.updateAssocDirectorPosition

        const updateAssocDirectorPositionBody = {
            midtermAppointmentDate: this.formatDateOrNull(midtermAppointmentDate),
            midtermTerminationDate: this.formatDateOrNull(midtermTerminationDate),
            version: version,
            acceptedWarnings: params.acceptedWarnings,
        }

        const apiResponseJSON: ApiResponse<void> = await this.http.put(
            `/api/admin/condominiums/${params.condominiumCd}/assocs/${params.assocId}/terms/${params.assocTermId}`
            + `/assoc-directors/${params.assocDirectorId}/positions/${params.assocDirectorPositionId}`,
            updateAssocDirectorPositionBody,
        )
        const apiResponse: ApiResponse<void> = ApiResponse.from(apiResponseJSON)

        return apiResponse.informations
    }

    async deleteAssocDirectorPosition(params: {
        condominiumCd: string,
        assocId: number,
        assocTermId: number,
        assocDirectorId: number,
        assocDirectorPositionId: number,
    }): Promise<Information> {
        const apiResponseJSON: ApiResponse<void> = await this.http.delete(
            `/api/admin/condominiums/${params.condominiumCd}/assocs/${params.assocId}/terms/${params.assocTermId}`
            + `/assoc-directors/${params.assocDirectorId}/positions/${params.assocDirectorPositionId}`,
        )
        const apiResponse: ApiResponse<void> = ApiResponse.from(apiResponseJSON)

        // TODO 配列の長さチェックを追加するか相談する
        return apiResponse.informations[0]
    }
}
